import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Gift, BookOpen, Check, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { CURRENT_GIFT_CHAPTER } from '../data/gift-chapter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GiftPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [meta, setMeta] = useState(CURRENT_GIFT_CHAPTER);

  // Refresh meta from backend (in case the live month is different from the bundled file)
  useEffect(() => {
    fetch(`${API_URL}/api/gift/current`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && data.title) {
          setMeta((m) => ({ ...m, ...{
            month: data.month,
            chapterNumber: data.chapter_number,
            title: data.title,
            subtitle: data.subtitle,
            amazonUrl: data.amazon_url || m.amazonUrl,
          }}));
        }
      })
      .catch(() => { /* fall back to bundled meta */ });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Please enter your email'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/gift/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, source: 'gift_page' }),
      });
      if (!res.ok) throw new Error('signup failed');
      // Mark as unlocked & route to reader
      localStorage.setItem('tav_gift_unlocked', '1');
      if (name) localStorage.setItem('tav_gift_name', name);
      navigate('/gift/read');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="gift-page" className="gift-page">
      {/* Subtle floating particles */}
      <div className="gift-particles" aria-hidden="true">
        {Array.from({ length: 10 }).map((_, i) => (
          <span key={i} className={`gift-particle gift-particle--${i + 1}`} />
        ))}
      </div>

      <div className="gift-inner">
        {/* Eyebrow + Month badge */}
        <div className="gift-eyebrow-row">
          <span className="gift-month-badge" data-testid="gift-month-badge">
            <Gift size={14} strokeWidth={2} /> {meta.month} · Free Gift
          </span>
        </div>

        {/* Two-column hero */}
        <div className="gift-hero">
          <div className="gift-cover-wrap">
            <img
              className="gift-cover"
              src={meta.bookCover}
              alt="F.U.C.K. T.H.A.T.T. — What Do I Want NOW! by Cabrini Schnyder"
              data-testid="gift-cover"
            />
            <div className="gift-cover-glow" aria-hidden="true" />
          </div>

          <div className="gift-text">
            <p className="gift-eyebrow">This Month's Free Chapter</p>
            <h1 className="gift-title" data-testid="gift-title">
              {meta.title}
            </h1>
            <p className="gift-subtitle">{meta.subtitle}</p>

            <ul className="gift-bullets" data-testid="gift-bullets">
              <li><Check size={16} strokeWidth={2.5} /> Read instantly — no waiting</li>
              <li><Check size={16} strokeWidth={2.5} /> A new chapter every month, always free</li>
              <li><Check size={16} strokeWidth={2.5} /> Loved it? Get the full book on Amazon</li>
            </ul>

            <form className="gift-form" onSubmit={handleSubmit} data-testid="gift-form">
              <input
                type="text"
                placeholder="Your first name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="gift-input"
                data-testid="gift-name"
              />
              <div className="gift-email-row">
                <Mail size={18} className="gift-email-icon" />
                <input
                  type="email"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="gift-input gift-input--email"
                  data-testid="gift-email"
                  required
                />
              </div>
              <button
                type="submit"
                className="gift-submit"
                data-testid="gift-submit"
                disabled={submitting}
              >
                {submitting ? 'Opening your gift...' : (
                  <><BookOpen size={16} strokeWidth={2} /> Send Me the Free Chapter</>
                )}
              </button>
              <p className="gift-privacy">Your email is sacred. We honor your privacy.</p>
            </form>

            {/* Already opted-in */}
            <p className="gift-already">
              Already on the list?{' '}
              <button
                type="button"
                className="gift-skip"
                data-testid="gift-skip"
                onClick={() => {
                  localStorage.setItem('tav_gift_unlocked', '1');
                  navigate('/gift/read');
                }}
              >
                Read it now <ArrowRight size={14} />
              </button>
            </p>
          </div>
        </div>

        {/* Bottom strip — Amazon CTA */}
        <div className="gift-amazon-strip" data-testid="gift-amazon-strip">
          <p className="gift-amazon-pre">Want the whole book?</p>
          <a
            href={meta.amazonUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="gift-amazon-btn"
            data-testid="gift-amazon-link"
          >
            Get F.U.C.K. T.H.A.T.T. on Amazon <ArrowRight size={14} />
          </a>
        </div>

        <div className="gift-back-row">
          <Link to="/" className="gift-back" data-testid="gift-back">
            ← Back to The Village
          </Link>
        </div>
      </div>
    </section>
  );
}
